import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  required,
  moneySubUnitAmountAtLeast,
  composeValidators,
  bookingDatesRequired,
  moneySubUnitAmountEqualToSpecificValueOrAtLeast,
  bookingConflictWithBookedDays,
} from '../../util/validators';
import { Form, PrimaryButton, FieldDateRangeInput, FieldCurrencyInput } from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './ExtendRentalForm.css';
import config from '../../config';
import { formatMoney } from '../../util/currency';
import { daysBetween, dateFromAPIToLocalNoon } from '../../util/dates';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';
import debounce from 'lodash/debounce';
import { isInclusivelyAfterDay, isInclusivelyBeforeDay, isSameDay } from 'react-dates';
import moment from 'moment';
import { isDayNotAbleToBook } from '../../components/FieldDateRangeInput/DateRangeInput.helpers';
import get from 'lodash/get';

const DEBOUNCE_WAIT_TIME = 600;

const identity = v => v;

const { Money } = sdkTypes;

const TODAY = moment();

const getBookedDays = (timeSlots, bookingEnd) => {
  if (!timeSlots) {
    return [];
  }

  const result = [];

  // get booked days before booking end
  // in case those days still have seats (booking available)
  const daysBeforeBookingEnd = timeSlots.reduce((acc, t) => {
    if(moment(bookingEnd).diff(moment(t.attributes.start), 'days') > 0) {
      return [...acc, t.attributes.start]
    }
    return acc;
  }, []);
  
  if (daysBeforeBookingEnd && daysBeforeBookingEnd.length > 0) {
    return daysBeforeBookingEnd;
  }

  const today = dateFromAPIToLocalNoon(moment().utc().startOf('day').toDate());
  const firstAvailableTimeslot = dateFromAPIToLocalNoon(timeSlots[0].attributes.start);
  const diffFromToday = moment(firstAvailableTimeslot).diff(moment(today), 'days');

  if (diffFromToday > 0) {
    for (let t = 0; t < diffFromToday; t++) {
      result.push(
        moment(today)
          .add(t, 'day')
          .toDate()
      );
    }
  }

  for (let i = 0; i < timeSlots.length - 1; i++) {
    const before = dateFromAPIToLocalNoon(timeSlots[i].attributes.start);
    const after = dateFromAPIToLocalNoon(timeSlots[i + 1].attributes.start);

    const diff = moment(after).diff(moment(before), 'days');

    if (diff === 1) {
      continue;
    }

    for (let t = 1; t < diff; t++) {
      result.push(
        moment(before)
          .add(t, 'day')
          .toDate()
      );
    }
  }

  return result;
};

const ExtendRentalFormComponent = props => {
  const [isFetchedValue, setIsFetchedValue] = useState(null);

  useEffect(() => {
    const { formId } = props;

    const startDateInp = document.getElementById(`${formId}.bookingStartDate`);
    startDateInp.classList.add(css.bannedInp);
    startDateInp.disabled = true;
  }, []);

  const handleOnChange = formValues => {
    const { currentRentalSeats, listingId, transactionId, isOwnListing } = props;
    const { bookingDates, price } = formValues ? formValues.values : {};
    const { startDate, endDate } = bookingDates || {};

    if (startDate && endDate && price && !props.fetchLineItemsInProgress) {
      const bookingParams = {
        bookingData: {
          startDate,
          endDate,
          extendPrice: price,
          seats: currentRentalSeats,
          transactionId: transactionId.uuid,
        },
        listingId,
        isOwnListing,
      };

      props.onFetchExtendTransactionLineItem(bookingParams);
      setIsFetchedValue({ bookingDates, price });
    }
  };

  const debouncedOnChange = debounce(handleOnChange, DEBOUNCE_WAIT_TIME, {
    leading: false,
    trailing: true,
  });

  return (
    <FinalForm
      {...props}
      initialValues={isFetchedValue ? isFetchedValue : props.initialValues}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          invalid,
          timeSlots,
          values,
          form,
          lineItems,
          fetchLineItemsInProgress,
          fetchLineItemsError,
          extendInProgress,
          extendError,
          unitType,
          formId,
          bookingEnd,
          currentRentalSeats,
          isProvider,
          createHoldingSlotTxInProgress,
          createHoldingSlotTxError,
          listing,
        } = fieldRenderProps;

        const { startDate, endDate } = (values && values.bookingDates) || {};
        const { minRent, pricingType } = get(listing, 'attributes.publicData', {});
        const { price } = values || {};
        const bookedDays = getBookedDays(timeSlots, bookingEnd);

        const isDayBlocked = startDate
          ? day => {
            return bookedDays.find(days => {
              const localStartDate = dateFromAPIToLocalNoon(days);
              return isSameDay(day, moment(localStartDate));
            });
          }
          : null;

        const isDayHighlighted = day =>
          isDayNotAbleToBook(bookedDays, timeSlots, minRent, pricingType)(day);

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = fetchLineItemsInProgress ||
          extendInProgress ||
          createHoldingSlotTxInProgress;

        const submitDisabled = invalid || disabled || submitInProgress;

        const numOfDays = startDate && endDate ? daysBetween(startDate, endDate) : null;

        const priceRequired = required(
          intl.formatMessage({
            id: 'ExtendRentalForm.priceRequired',
          })
        );
        const minPrice = new Money(config.extendMinimumPriceSubUnit, config.currency);
        const zeroPrice = new Money(config.extendZeroPriceSubUnit, config.currency);
        const minPriceRequired = moneySubUnitAmountAtLeast(
          intl.formatMessage(
            {
              id: 'ExtendRentalForm.priceTooLow',
            },
            {
              minPrice: formatMoney(intl, minPrice),
            }
          ),
          config.extendMinimumPriceSubUnit
        );
        const equalAndMinPriceRequired = moneySubUnitAmountEqualToSpecificValueOrAtLeast(
          intl.formatMessage(
            {
              id: 'ExtendRentalForm.priceNotEqualOrTooLow',
            },
            {
              zeroPrice: formatMoney(intl, zeroPrice),
              minPrice: formatMoney(intl, minPrice),
            }
          ),
          config.extendMinimumPriceSubUnit
        );

        const priceValidators =
          numOfDays && numOfDays <= 5
            ? composeValidators(priceRequired, equalAndMinPriceRequired)
            : composeValidators(priceRequired, minPriceRequired);

        const priceLabel = intl.formatMessage({ id: 'ExtendRentalForm.priceLabel' });
        const pricePlaceHolder = intl.formatMessage({ id: 'ExtendRentalForm.pricePlaceHolder' });
        const extendBookingEndPlaceHolder = intl.formatMessage({
          id: 'ExtendRentalForm.extendBookingEndPlaceHolder',
        });
        const extendBookingStartPlaceHolder = intl.formatMessage({
          id: 'ExtendRentalForm.extendBookingStartPlaceHolder',
        });
        const requiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.requiredDate',
        });
        const startDateErrorMessage = intl.formatMessage({
          id: 'ExtendRentalForm.bookingStartDateRequiredMessage',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'ExtendRentalForm.bookingEndDateRequiredMessage',
        });
        const extendStartDateLabel = intl.formatMessage({
          id: 'ExtendRentalForm.extendStartDateLabel',
        });
        const extendEndDateLabel = intl.formatMessage({
          id: 'ExtendRentalForm.extendEndDateLabel',
        });
        const bookingConflictErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.bookingConflictErrorMessage',
        });

        const bookingData =
          startDate && endDate
            ? {
              unitType: config.bookingUnitType,
              startDate,
              endDate,
              seats: currentRentalSeats,
            }
            : null;

        const showEstimatedBreakdown =
          bookingData && lineItems && price && !fetchLineItemsInProgress && !fetchLineItemsError;

        const bookingInfoMaybe = showEstimatedBreakdown ? (
          <div className={css.priceBreakdownContainer}>
            <EstimatedBreakdownMaybe
              userRole="provider"
              bookingData={bookingData}
              lineItems={lineItems}
            />
          </div>
        ) : null;

        const bookingInfoErrorMaybe =
          fetchLineItemsError || extendError || createHoldingSlotTxError ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="ExtendRentalForm.fetchLineItemsError" />
            </span>
          ) : null;

        const endOfRange = config.dayCountAvailableForBooking - 1;
        const onDateAfterStartDate = startDate ? moment(startDate).add(1, 'days') : null;
        const blockDay = moment(bookingEnd);
        // onDateAfterStartDate && TODAY.isBefore(onDateAfterStartDate)
        //   ? onDateAfterStartDate
        //   : moment(bookingEnd);

        const isOutsideRange = day =>
          startDate
            ? !isInclusivelyBeforeDay(
              day,
              moment(startDate)
                .clone()
                .add(endOfRange, 'days')
            ) || !isInclusivelyAfterDay(day, moment())
            : null;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FormSpy
              subscription={{ values: true }}
              onChange={values => {
                const { endDate: newEndDate } =
                  values.values.bookingDates || {};

                let reRenderPriceInp = false;
                if (
                  newEndDate &&
                  endDate &&
                  (newEndDate.toString() !== endDate.toString())
                ) {
                  reRenderPriceInp = true;
                }

                if (reRenderPriceInp) {
                  form.change('price', undefined);
                  setIsFetchedValue({ bookingDates: values.values.bookingDates });
                }

                debouncedOnChange(values, reRenderPriceInp);
              }}
            />

            <FieldDateRangeInput
              className={css.bookingDates}
              name="bookingDates"
              unitType={unitType}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={extendStartDateLabel}
              startDatePlaceholderText={extendBookingStartPlaceHolder}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={extendEndDateLabel}
              endDatePlaceholderText={extendBookingEndPlaceHolder}
              format={identity}
              timeSlots={timeSlots}
              useMobileMargins
              validate={composeValidators(
                required(requiredMessage),
                bookingDatesRequired(startDateErrorMessage, endDateErrorMessage),
                bookingConflictWithBookedDays(bookingConflictErrorMessage, bookedDays)
              )}
              isOutsideRange={isOutsideRange}
              mobileMarginsClassname={css.mobileMargins}
              isDayBlocked={isDayBlocked}
              isDayHighlighted={isDayHighlighted}
            />

            {isProvider ? (
              <>
                {values.bookingDates &&
                  values.bookingDates.startDate &&
                  values.bookingDates.endDate && (
                    <FieldCurrencyInput
                      id="price"
                      name="price"
                      className={css.priceInput}
                      autoFocus
                      label={priceLabel}
                      placeholder={pricePlaceHolder}
                      currencyConfig={config.currencyConfig}
                      validate={priceValidators}
                      isExtendForm
                    />
                  )}

                <p className={css.priceNote}>
                  <FormattedMessage
                    id="ExtendRentalForm.priceNote"
                    values={{ minPrice: formatMoney(intl, minPrice) }}
                  />
                </p>
              </>
            ) : null}

            {bookingInfoMaybe}
            {bookingInfoErrorMaybe}

            <PrimaryButton
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="ExtendRentalForm.submit" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

ExtendRentalFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  unitType: config.bookingUnitType,
};

const { func, string } = PropTypes;

ExtendRentalFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  unitType: propTypes.bookingUnitType,
};

const ExtendRentalForm = compose(injectIntl)(ExtendRentalFormComponent);
ExtendRentalForm.displayName = 'ExtendRentalForm';

export default ExtendRentalForm;
